import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./AddComponent.css";
import { API_BASE_URL } from "../config/api";
import Seo from "../Components/Seo";
import { authedFetch } from "../services/apiClient";

const MAX_IMAGES = 6;

export default function EditProduct() {
  const { productId } = useParams();
  const navigate = useNavigate();

  /* ---------------- FORM ---------------- */
  const [productName, setProductName] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [pinCode, setPinCode] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");

  /* ---------------- LOCATION ---------------- */
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState("");
  const [selectedCityId, setSelectedCityId] = useState("");

  /* ---------------- CATEGORY ---------------- */
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubCategoryId, setSelectedSubCategoryId] = useState("");

  /* ---------------- MEDIA ---------------- */
  const [images, setImages] = useState([]); // string(url) | File
  const [video, setVideo] = useState(null);

  /* ---------------- UI ---------------- */
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");

  const mobileNo = localStorage.getItem("userMobileNumber") || "";

  /* ---------------- HELPERS ---------------- */
  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  /* ---------------- INITIAL LOAD ---------------- */
  useEffect(() => {
    fetchStates();
    fetchCategories();
    fetchProductDetails();
    // eslint-disable-next-line
  }, []);

  const fetchStates = async () => {
    const res = await fetch(`${API_BASE_URL}/State/GetStates`);
    setStates(await res.json());
  };

  const fetchCities = async (id) => {
    const res = await fetch(
      `${API_BASE_URL}/City/GetCitiesByState?id=${id}`
    );
    setCities(await res.json());
  };

  const fetchCategories = async () => {
    const res = await fetch(
      `${API_BASE_URL}/Category/GetCategories`
    );
    setCategories(await res.json());
  };

  const fetchSubCategories = async (id) => {
    const res = await fetch(
      `${API_BASE_URL}/SubCategory/GetSubCategoriesByCategory?id=${id}`
    );
    setSubCategories(await res.json());
  };

  /* ---------------- FETCH PRODUCT ---------------- */
  const fetchProductDetails = async () => {
    try {
      setLoading(true);
      const res = await authedFetch(
        `${API_BASE_URL}/product/GetProductByIdForEdit/${productId}`
      );
      if (!res.ok) throw new Error("load failed");
      const raw = await res.json();
      // Endpoint object deta hai; kabhi kabhi array[0] bhi aa sakta hai.
      const data = Array.isArray(raw) ? raw[0] || {} : raw;
      console.log("EDIT-LOAD data:", data);

      setProductName(data.ProductName || "");
      setPrice(data.Price?.toString() || "");
      setQuantity(data.Quantity?.toString() || "1");
      setPinCode(data.Pincode?.toString() || "");
      setAddress(data.Address || "");
      setDescription(data.Description || "");

      setSelectedStateId(data.StateId || "");
      setSelectedCityId(data.CityId || "");
      setSelectedCategoryId(data.CategoryId || "");
      setSelectedSubCategoryId(data.SubCategoryId || "");

      if (data.StateId) fetchCities(data.StateId);
      if (data.CategoryId) fetchSubCategories(data.CategoryId);

      setImages(
        [
          data.Image1,
          data.Image2,
          data.Image3,
          data.Image4,
          data.Image5,
          data.Image6,
        ].filter(Boolean)
      );

    } catch (e) {
      setError("Failed to load product details");
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- IMAGES ---------------- */
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length > MAX_IMAGES) return;
    setImages((prev) => [...prev, ...files]);
  };

  const removeImage = (i) =>
    setImages(images.filter((_, index) => index !== i));

  /* ---------------- SUBMIT ---------------- */
const handleSubmit = async () => {
  setError("");

  if (!mobileNo) return alert("Login required");
  if (!productName || !price)
    return setError("Product name & price required");

  const mobilePattern = /\b\d{10}\b/;
  if (mobilePattern.test(description + address))
    return setError("Do not add mobile number in description/address");

  setLoading(true);
  setProgress(10);

  try {
    const payload = {
      Id: productId,
      MobileNo: mobileNo,
      ProductName: productName,
      Price: Number(price),
      Quantity: Number(quantity) || 1,
      PinCode: pinCode ? Number(pinCode) : 0,
      State: selectedStateId ? Number(selectedStateId) : null,
      City: selectedCityId ? Number(selectedCityId) : null,
      Category: selectedCategoryId ? Number(selectedCategoryId) : null,
      SubCategory: selectedSubCategoryId ? Number(selectedSubCategoryId) : null,
      Address: address || "",
      Description: description || "",
      Image1: null,
      Image2: null,
      Image3: null,
      Image4: null,
      Image5: null,
      Image6: null,
      Video: null,
    };

    // 🔥 IMPORTANT FIX
    for (let i = 0; i < images.length; i++) {
      const img = images[i];

      if (img instanceof File) {
        // ✅ ONLY NEW IMAGE → BASE64
        payload[`Image${i + 1}`] = await fileToBase64(img);
      } 
      // ❌ URL IMAGES → SKIP (KEEP NULL)
    }

    console.log("FINAL PAYLOAD:", payload);

    const res = await authedFetch(
      `${API_BASE_URL}/product/EditProduct`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (!res.ok) {
      const err = await res.json();
      console.error(err);
      throw new Error("Update failed");
    }

    alert("Product updated successfully!");
    navigate("/myadds");

  } catch (e) {
    console.error(e);
    setError("Update failed");
  } finally {
    setLoading(false);
    setProgress(0);
  }
};


  /* ---------------- UI ---------------- */
  return (
    <div className="add-product-page">
      <Seo title="Edit Product — Update Your Ad" description="AIO India pe post kiye hue ad ko edit karein — naam, price, quantity, photos, description sab update karein." path={`/edit-product/${productId}`} />
      <div className="card">
        <h2>Edit Product</h2>

        {error && <p className="error">{error}</p>}

        <div className="form-grid">
          <input value={productName} onChange={(e) => setProductName(e.target.value)} placeholder="Product Name" />
          <input value={price} type="number" onChange={(e) => setPrice(e.target.value)} placeholder="Price" />
          <input value={quantity} type="number" min="1" onChange={(e) => setQuantity(e.target.value)} placeholder="Quantity" />
          <input value={pinCode} maxLength={6} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, ""))} placeholder="Pincode" />

          <select value={selectedStateId} onChange={(e) => {
            setSelectedStateId(e.target.value);
            fetchCities(e.target.value);
          }}>
            <option value="">Select State</option>
            {states.map(s => <option key={s.Id ?? s.id} value={s.Id ?? s.id}>{s.Name ?? s.name}</option>)}
          </select>

          <select value={selectedCityId} onChange={(e) => setSelectedCityId(e.target.value)}>
            <option value="">Select City</option>
            {cities.map(c => <option key={c.Id ?? c.id} value={c.Id ?? c.id}>{c.Name ?? c.name}</option>)}
          </select>

          <select value={selectedCategoryId} onChange={(e) => {
            setSelectedCategoryId(e.target.value);
            fetchSubCategories(e.target.value);
          }}>
            <option value="">Select Category</option>
            {categories.map(c => <option key={c.Id ?? c.id} value={c.Id ?? c.id}>{c.Name ?? c.name}</option>)}
          </select>

          <select value={selectedSubCategoryId} onChange={(e) => setSelectedSubCategoryId(e.target.value)}>
            <option value="">Select Sub-Category</option>
            {subCategories.map(sc => <option key={sc.Id ?? sc.id} value={sc.Id ?? sc.id}>{sc.Name ?? sc.name}</option>)}
          </select>
        </div>

        <textarea value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Address" />
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" />

        <input type="file" multiple accept="image/*" onChange={handleImageChange} />

        <div className="image-grid">
          {images.map((img, i) => (
            <div key={i} className="image-box">
              <img src={img instanceof File ? URL.createObjectURL(img) : img} alt="" />
              <button onClick={() => removeImage(i)}>✕</button>
            </div>
          ))}
        </div>

        {loading && (
          <div className="progress">
            Uploading… {progress}%
            <div className="bar"><div style={{ width: `${progress}%` }} /></div>
          </div>
        )}

        <button className="submit-btn" onClick={handleSubmit} disabled={loading}>
          {loading ? "Updating..." : "Update Product"}
        </button>
      </div>
    </div>
  );
}
